import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Info, RotateCcw } from "lucide-react";
import { FilterSelect } from "./FilterSelect";
import { StoneCard } from "./StoneCard";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { Reveal, SplitLines } from "./Reveal";
import { fetchOptions, fetchStones } from "../lib/api";

export const useStoneFilters = (initialShape = "") => {
  const [filters, setFilters] = useState({ shape: initialShape, sieve: "", clarity: "", carat: "" });
  const set = (key, value) => {
    setFilters((f) => {
      const n = { ...f, [key]: value };
      if (key === "sieve" && value) n.carat = "melee";
      if (key === "carat" && value && value !== "melee") n.sieve = "";
      return n;
    });
  };
  return [filters, set, () => setFilters({ shape: "", sieve: "", clarity: "", carat: "" })];
};

export const StoneBrowser = ({ externalShape }) => {
  const [sp] = useSearchParams();
  const q = sp.get("q") || "";
  const [options, setOptions] = useState(null);
  const [filters, setFilter, reset] = useStoneFilters(externalShape);
  const [data, setData] = useState({ total: 0, total_available_carats: 0, stones: [] });
  const [loading, setLoading] = useState(true);
  const reqId = useRef(0);

  useEffect(() => { fetchOptions().then(setOptions).catch(() => {}); }, []);
  useEffect(() => { if (externalShape) setFilter("shape", externalShape); }, [externalShape]); // eslint-disable-line
  useEffect(() => {
    setLoading(true);
    const id = ++reqId.current;
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (q) params.q = q;
    fetchStones(params).then((d) => { if (id === reqId.current) setData(d); }).catch(() => {}).finally(() => { if (id === reqId.current) setLoading(false); });
  }, [filters, q]);

  const note = (list, val) => list?.find((o) => o.value === val)?.note;
  const notes = options ? [
    ["Shape", note(options.shapes, filters.shape)],
    ["Sieve / mm", note(options.sieves, filters.sieve)],
    ["Clarity", note(options.clarities, filters.clarity)],
    ["Carat", note(options.carats, filters.carat)],
  ].filter(([, n]) => n) : [];

  return (
    <section id="browse" data-testid="browse-section" className="relative py-24 lg:py-36">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-end mb-14">
          <div className="lg:col-span-7">
            <Reveal><p className="eyebrow mb-6">01 — Browse Stones</p></Reveal>
            <h2 className="font-display font-light text-ivory text-[clamp(2.4rem,5.5vw,5rem)] leading-[0.95]">
              <SplitLines inView lines={["Every parcel,", <span key="a" className="text-saffron italic">carat-wise.</span>]} />
            </h2>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5 text-ivory/60 font-light text-base leading-relaxed">
            Melee is sold by the carat, solitaires by the stone. Filter CVD and HPHT lab grown diamonds by shape, sieve size in millimetres, clarity and carat — every
            selection tells you <span className="text-ivory">what matters</span> for that choice. Quotes, parcel photos and availability
            are confirmed on <WhatsAppIcon size={16} className="text-[#25D366]" />.
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4 xl:col-span-3">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="grid sm:grid-cols-2 lg:grid-cols-1 gap-5 bg-[#0e0e0e] border border-gold/15 p-5" data-testid="stone-filters">
                <FilterSelect id="filter-shape" label="Shape" value={filters.shape} onChange={(v) => setFilter("shape", v)} options={options?.shapes || []} placeholder="All shapes" />
                <FilterSelect id="filter-sieve" label="Sieve · Approx. mm" value={filters.sieve} onChange={(v) => setFilter("sieve", v)} options={options?.sieves || []} placeholder="All sizes" />
                <FilterSelect id="filter-clarity" label="Clarity" value={filters.clarity} onChange={(v) => setFilter("clarity", v)} options={options?.clarities || []} placeholder="All clarities" />
                <FilterSelect id="filter-carat" label="Carat range" value={filters.carat} onChange={(v) => setFilter("carat", v)} options={options?.carats || []} placeholder="All carats" />
                <button data-testid="filters-reset-btn" onClick={reset} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ivory/50 hover:text-saffron transition-colors sm:col-span-2 lg:col-span-1">
                  <RotateCcw size={13} /> Reset filters
                </button>
              </div>

              <AnimatePresence mode="popLayout">
                {notes.length > 0 && (
                  <motion.div
                    key="notes" data-testid="what-matters-notes"
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }}
                    className="border-l-2 border-saffron bg-gold/5 p-5 space-y-4"
                  >
                    <p className="eyebrow flex items-center gap-2"><Info size={13} /> What matters</p>
                    {notes.map(([k, n]) => (
                      <div key={k}>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-gold">{k}</p>
                        <p className="text-sm text-ivory/80 leading-relaxed mt-1 font-light">{n}</p>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </aside>

          <div className="lg:col-span-8 xl:col-span-9">
            <div className="flex flex-wrap items-baseline justify-between gap-4 mb-6 border-b border-gold/15 pb-4">
              <p className="text-sm text-ivory/60" data-testid="results-summary">
                <span className="font-display text-3xl text-saffron mr-2">~{Number(data.total_available_carats).toLocaleString("en-IN")}</span> ct available in this selection
                {q && <span className="block mt-1 text-xs text-gold" data-testid="search-query-label">Search: “{q}” · <Link to="/browse" className="underline underline-offset-4 hover:text-saffron">clear</Link></span>}
              </p>
              <Link to={`/enquiry?${new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v))).toString()}`} data-testid="browse-enquire-selection-btn" className="btn-outline !py-2.5 !px-5 text-[11px]">
                Enquire this selection
              </Link>
            </div>

            <div className={`grid sm:grid-cols-2 xl:grid-cols-3 gap-4 transition-opacity duration-300 ${loading ? "opacity-40" : "opacity-100"}`} data-testid="stone-results">
              {data.stones.map((lot, i) => <StoneCard key={lot.id} lot={lot} index={i} />)}
            </div>
            {!loading && data.total === 0 && (
              <p data-testid="no-results" className="text-ivory/60 py-16 text-center font-light">No parcels match this exact combination — send us the spec and we will source it within days.</p>
            )}
            {data.total > data.stones.length && (
              <p className="mt-8 text-xs text-ivory/40 text-center">Showing the first {data.stones.length} parcels. Narrow the filters or enquire for the full list.</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
